/**
 * bundle config needed to setup in app.config.ts
 *
 * @example app.config.ts
 * ```ts
 * import { provideI18n } from '@evc/platform';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideI18n(),
 *   ],
 * };
 * ```
 *
 * @example some.service.ts
 * ```ts
 * export class SomeService {
 *   constructor(
 *    private readonly _i18n: I18nService,
 *   ) {
 *      this._i18n.init();
 *   }
 * }
 * ```
 */
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import type { EnvironmentProviders, Provider } from '@angular/core';
import { importProvidersFrom, LOCALE_ID, provideAppInitializer } from '@angular/core';

import { LanguageInterceptor, LanguageService } from '@evc/platform';

import { I18NLoader } from './i18n.loader';
import { TranslateLoader, TranslatePipe } from './i18n.service';

export function localeFactory(languageService: LanguageService): string {
  return languageService.locale();
}

// TODO improving with async loading only for current locale
export function registerLocales(): void {
  registerLocaleData(localeFr, 'fr-FR');
  registerLocaleData(localeEn, 'en-US');
}

export function getI18nProviders(): (Provider | EnvironmentProviders)[] {
  return [
    provideAppInitializer(() => registerLocales()),
    importProvidersFrom(
      TranslatePipe.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: I18NLoader,
          deps: [HttpClient],
        },
      }),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: localeFactory,
      deps: [LanguageService],
    },
  ];
}
